/**
 * ProtectedRoute
 * Componente que protege rutas verificando autenticación y permisos.
 *
 * HIPAA Requirement: 45 CFR 164.308(a)(4) - Information Access Management
 *
 * ============================================================================
 * DOCUMENTACIÓN PARA REFACTORIZACIÓN CON BACKEND
 * ============================================================================
 *
 * ESTADO ACTUAL:
 * - Verifica autenticación (user existe)
 * - Verifica permisos usando PermissionsContext (frontend)
 * - Redirige a /Login si no autenticado
 * - Redirige a /Unauthorized si no tiene permisos
 *
 * CAMBIOS REQUERIDOS EN BACKEND:
 * 1. Validar permisos en cada endpoint de API (no confiar solo en frontend)
 * 2. Retornar 403 Forbidden si el usuario no tiene permisos
 * 3. Implementar middleware de autorización en el backend
 *
 * ============================================================================
 */

import React, { useContext } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext/AuthContext";
import { usePermissions } from "../../context/PermissionsContext";

/**
 * ProtectedRoute - Protege rutas por autenticación y permisos
 *
 * @param {string} requiredPermission - Permiso específico requerido (opcional)
 * @param {string[]} requiredPermissions - Lista de permisos requeridos (opcional)
 * @param {boolean} requireAll - Si true, requiere TODOS los permisos; si false, requiere AL MENOS UNO
 */
const ProtectedRoute = ({
  requiredPermission,
  requiredPermissions,
  requireAll = false,
}) => {
  const { user } = useContext(AuthContext);
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const location = useLocation();

  // 1. Verificar autenticación
  if (!user) {
    return <Navigate to="/Login" state={{ from: location }} replace />;
  }

  // 1.b HIPAA 45 CFR §164.312(d): sin 2FA activo el usuario no puede ver la
  //     aplicación. Lo mandamos a la página-bloqueo hasta que enrole.
  //     Usuarios con cambio de contraseña pendiente siguen yendo primero a
  //     /auth/change-password — ese flujo está controlado en LoginPage y las
  //     rutas /auth/change-password y /ChangePassword no están dentro de este
  //     ProtectedRoute, por lo que no hay conflicto.
  if (user.mfaEnabled !== true) {
    return <Navigate to="/mfa-required" replace />;
  }

  // 2. Verificar permiso específico si se proporciona
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/Unauthorized" state={{ from: location }} replace />;
  }

  // 3. Verificar lista de permisos si se proporciona
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasAccess) {
      return <Navigate to="/Unauthorized" state={{ from: location }} replace />;
    }
  }

  // 4. La autorización por ruta la gobierna RoleGuard usando user.modules (RBAC
  //    real del backend), que envuelve cada ruta. Antes aquí había un gate
  //    hardcodeado (ROUTE_PERMISSIONS + ROLE_PERMISSIONS por rol): un rol NUEVO
  //    creado en el backend no estaba en el mapa, caía a GUEST y se le negaba el
  //    acceso AUNQUE el admin le hubiera activado el módulo en la matriz RBAC.
  //    Se elimina ese gate redundante: ProtectedRoute solo cubre autenticación
  //    y MFA; el acceso a módulos lo decide RoleGuard contra el backend.

  return <Outlet />;
};

export default ProtectedRoute;
